import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { db } from '../firebaseConfig';
import { collection, doc, setDoc, serverTimestamp, deleteDoc, getDocs, CollectionReference, DocumentData } from 'firebase/firestore';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// 두 자리로 맞추는 함수
function pad2(n: number | string) {
  n = Number(n);
  return n < 10 ? '0' + n : '' + n;
}

// 엑셀 시리얼값을 yyyy-MM-dd로 변환
function excelDateToString(serial: number) {
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);
  const year = date_info.getUTCFullYear();
  const month = pad2(date_info.getUTCMonth() + 1);
  const day = pad2(date_info.getUTCDate());
  return `${year}-${month}-${day}`;
}

// 어떤 타입이든 yyyy-MM-dd로 변환
function normalizeDateString(date: any) {
  if (typeof date === 'number') {
    return excelDateToString(date);
  } else if (date instanceof Date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } else if (typeof date === 'string') {
    // yyyy-M-d, yyyy-MM-d, yyyy-M-dd 등 다양한 경우를 yyyy-MM-dd로 변환
    const match = date.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      const y = match[1];
      const m = pad2(match[2]);
      const d = pad2(match[3]);
      return `${y}-${m}-${d}`;
    }
    return date.trim();
  }
  return '';
}

const ExcelImport: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [routeHistoryLoading, setRouteHistoryLoading] = useState(false);
  const [routeStatsLoading, setRouteStatsLoading] = useState(false);
  const [deleteStartDate, setDeleteStartDate] = useState<Date | null>(null);
  const [deleteEndDate, setDeleteEndDate] = useState<Date | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // 통합일보 미사용으로 운송구간·운송통계 비활성화, 기본은 자동완성
  const [activeTab, setActiveTab] = useState<'route' | 'stats' | 'autocomplete'>('autocomplete');
  const [routeHistoryJobId, setRouteHistoryJobId] = useState<string | null>(null);
  const [routeHistoryJobStatus, setRouteHistoryJobStatus] = useState<'pending' | 'running' | 'success' | 'error' | null>(null);
  const [routeHistoryJobError, setRouteHistoryJobError] = useState<string | null>(null);
  const percentRef = useRef<number>(0);

//  function getDateRangeArray(start: Date, end: Date) {
//    const arr = [];
//    let dt = new Date(start);
//    while (dt <= end) {
//      arr.push(dt.toISOString().slice(0, 10));
//      dt.setDate(dt.getDate() + 1);
//    }
//    return arr;
//  }

  const tabStyles = {
    common: {
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: 600,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      borderBottom: '3px solid transparent',
      color: '#666',
      transition: 'all 0.2s ease-in-out',
    },
    active: {
      color: '#1976d2',
      borderBottom: '3px solid #1976d2',
    }
  };

  const buttonStyles: React.CSSProperties = {
    fontSize: 16,
    padding: '12px 32px',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    marginTop: 24,
    fontWeight: 'bold',
    transition: 'background 0.2s',
  };

  return (
    <div style={{ padding: '40px 60px', textAlign: 'center', background: '#f9fafb', minHeight: 'calc(100vh - 48px)' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ color: '#111', fontSize: 28, fontWeight: 'bold' }}>캐시 데이터베이스 관리</h1>
        </div>
        <div style={{ marginBottom: 32, color: '#555', fontSize: 16, lineHeight: 1.7 }}>
          데이터가 최신 버전이 필요한 경우, 아래 버튼을 통해 캐시 데이터베이스를 수동으로 갱신할 수 있습니다.<br />
          이 작업은 시스템에 저장된 최근 1년치 데이터를 기반으로 통계를 다시 계산하므로 시간이 소요될 수 있습니다.
          <br />
          1년치 데이터를 집계하므로 잦은 데이터베이스 생성을 ( 버튼클릭 ) 하면 요금이 많이 나옵니다 
          <br />
          권장사항은 일주일에 한~두번 버튼을 눌러 캐시를 생성하는 것입니다.
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', borderBottom: '1px solid #ddd', marginBottom: 32 }}>
          <button
            style={{
              ...tabStyles.common,
              ...(activeTab === 'route' ? tabStyles.active : {}),
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
            disabled
            title="통합일보 미사용으로 비활성화"
          >
            운송구간
          </button>
          <button
            style={{
              ...tabStyles.common,
              ...(activeTab === 'stats' ? tabStyles.active : {}),
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
            disabled
            title="통합일보 미사용으로 비활성화"
          >
            운송통계
          </button>
          <button
            style={{ ...tabStyles.common, ...(activeTab === 'autocomplete' ? tabStyles.active : {}) }}
            onClick={() => setActiveTab('autocomplete')}
          >
            자동완성
          </button>
        </div>

        <div style={{ background: '#fff', padding: '32px 40px', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.05)', textAlign: 'left' }}>
          {activeTab === 'route' && (
            <div>
              <h3 style={{ fontSize: 20, color: '#111', fontWeight: 'bold', marginBottom: 12 }}>운송구간 이력 캐시 (통합일보)</h3>
              <p style={{ fontSize: 15, color: '#555', lineHeight: 1.8 }}>
                '통합일보' 페이지에서 각 운송구간의 과거 데이터, 차량단가를 조회하는 데 사용됩니다. 
                통합일보의 운송구간 셀 클릭, 차량번호 클릭 시 나타나는 이력 창(평균, 최빈값, 최고/최저 운임 등)이 
                비정상적으로 보이거나 데이터가 최신이 아닐 경우 이 버튼을 눌러 캐시를 재생성하세요.
                
              </p>
              <div style={{ textAlign: 'center' }}>
                <button
                  style={{ ...buttonStyles, background: '#1976d2' }}
                  onClick={async () => {
                    if (!window.confirm('통합일보에 사용되는 운송구간 캐시를 갱신하시겠습니까?')) return;
                    setRouteHistoryLoading(true);
                    setRouteHistoryJobId(null);
                    setRouteHistoryJobStatus('pending');
                    setRouteHistoryJobError(null);
                    try {
                      const res = await fetch('/api/build-route-cache-async', { method: 'POST' });
                      const data = await res.json();
                      if (res.ok && data.jobId) {
                        setRouteHistoryJobId(data.jobId);
                        setRouteHistoryJobStatus('pending');
                        // 폴링 시작
                        let pollCount = 0;
                        const poll = async () => {
                          if (!data.jobId) return;
                          try {
                            const statusRes = await fetch(`/api/build-route-cache-status?jobId=${data.jobId}`);
                            const statusData = await statusRes.json();
                            setRouteHistoryJobStatus(statusData.status);
                            if (statusData.status === 'success') {
                              setRouteHistoryLoading(false);
                              alert(`운송구간 캐시(통합일보) 갱신 완료!\n처리된 데이터: ${statusData.totalProcessedRows || statusData.count || 0}건`);
                              return;
                            } else if (statusData.status === 'error') {
                              setRouteHistoryLoading(false);
                              setRouteHistoryJobError(statusData.error || '알 수 없는 오류');
                              alert('갱신 실패: ' + (statusData.error || '알 수 없는 오류'));
                              return;
                            } else {
                              pollCount++;
                              if (pollCount < 300) { // 최대 10분
                                setTimeout(poll, 2000);
                              } else {
                                setRouteHistoryLoading(false);
                                setRouteHistoryJobError('타임아웃');
                                alert('갱신이 너무 오래 걸려 중단되었습니다.');
                              }
                            }
                            if (statusData.percent !== undefined) {
                              percentRef.current = statusData.percent;
                            }
                          } catch (e) {
                            setRouteHistoryLoading(false);
                            setRouteHistoryJobError('상태 확인 실패');
                            alert('상태 확인 실패: ' + e);
                          }
                        };
                        setTimeout(poll, 2000);
                      } else {
                        setRouteHistoryLoading(false);
                        alert('갱신 요청 실패: ' + (data.error || '알 수 없는 오류'));
                      }
                    } catch (err) {
                      setRouteHistoryLoading(false);
                      alert('갱신 요청 실패: ' + err);
                    }
                  }}
                  disabled={loading || routeHistoryLoading || routeStatsLoading}
                >
                  {routeHistoryLoading ? '생성중...' : '운송구간 캐시 생성'}
                </button>
              </div>
            </div>
          )}
          {activeTab === 'stats' && (
            <div>
              <h3 style={{ fontSize: 20, color: '#111', fontWeight: 'bold', marginBottom: 12 }}>운송 통계 캐시 (검색창 통계)</h3>
              <p style={{ fontSize: 15, color: '#555', lineHeight: 1.8 }}>
                이 캐시는 '검색' 페이지의(돋보기모양) 통계 데이터를 위해 사용됩니다. 
                전체 운송 데이터에 대한 통계를 미리 계산하여 차종별 단가를 보여줍니다
                검색 페이지의 통계 데이터가 갱신되지 않을때 사용하세요.
              </p>
              <div style={{ textAlign: 'center' }}>
                <button
                  style={{ ...buttonStyles, background: '#388e3c' }}
                  onClick={async () => {
                    if (!window.confirm('검색창 통계에 사용되는 운송통계 캐시를 갱신하시겠습니까?')) return;
                    setRouteStatsLoading(true);
                    setRouteHistoryJobId(null);
                    setRouteHistoryJobStatus('pending');
                    setRouteHistoryJobError(null);
                    try {
                      const res = await fetch('/api/route-stats-cache/build-async', { method: 'POST' });
                      const data = await res.json();
                      if (res.ok && data.jobId) {
                        setRouteHistoryJobId(data.jobId);
                        setRouteHistoryJobStatus('pending');
                        // 폴링 시작
                        let pollCount = 0;
                        const poll = async () => {
                          if (!data.jobId) return;
                          try {
                            const statusRes = await fetch(`/api/route-stats-cache/build-status?jobId=${data.jobId}`);
                            const statusData = await statusRes.json();
                            setRouteHistoryJobStatus(statusData.status);
                            if (statusData.status === 'success') {
                              setRouteStatsLoading(false);
                              alert(`운송통계 캐시(검색창) 갱신 완료!\n처리된 데이터: ${statusData.totalProcessedRows || statusData.count || 0}건`);
                              return;
                            } else if (statusData.status === 'error') {
                              setRouteStatsLoading(false);
                              setRouteHistoryJobError(statusData.error || '알 수 없는 오류');
                              alert('갱신 실패: ' + (statusData.error || '알 수 없는 오류'));
                              return;
                            } else {
                              pollCount++;
                              if (pollCount < 300) { // 최대 10분
                                setTimeout(poll, 2000);
                              } else {
                                setRouteStatsLoading(false);
                                setRouteHistoryJobError('타임아웃');
                                alert('갱신이 너무 오래 걸려 중단되었습니다.');
                              }
                            }
                            if (statusData.percent !== undefined) {
                              percentRef.current = statusData.percent;
                            }
                          } catch (e) {
                            setRouteStatsLoading(false);
                            setRouteHistoryJobError('상태 확인 실패');
                            alert('상태 확인 실패: ' + e);
                          }
                        };
                        setTimeout(poll, 2000);
                      } else {
                        setRouteStatsLoading(false);
                        alert('갱신 요청 실패: ' + (data.error || '알 수 없는 오류'));
                      }
                    } catch (err) {
                      setRouteStatsLoading(false);
                      alert('갱신 요청 실패: ' + err);
                    }
                  }}
                  disabled={loading || routeHistoryLoading || routeStatsLoading}
                >
                  {routeStatsLoading ? '생성중...' : '운송통계 캐시 생성'}
                </button>
              </div>
            </div>
          )}
          {activeTab === 'autocomplete' && (
            <div>
              <h3 style={{ fontSize: 20, color: '#111', fontWeight: 'bold', marginBottom: 12 }}>운송구간 자동완성 캐시 (배차일보)</h3>
              <p style={{ fontSize: 15, color: '#555', lineHeight: 1.8 }}>
                '배차일보'에서 운송구간을 입력할 때 나타나는 자동완성 목록을 생성하는 캐시입니다.
                새로운 운송구간을 추가했음에도 자동완성 목록에 나타나지 않을 경우 이 버튼을 눌러
                캐시를 갱신할 수 있습니다.
              </p>
              <div style={{ textAlign: 'center' }}>
                <button
                  style={{ ...buttonStyles, background: '#ff9800' }}
                  onClick={async () => {
                    if (!window.confirm('배차일보에 사용되는 운송구간 자동완성 캐시를 갱신하시겠습니까?')) return;
                    setRouteStatsLoading(true);
                    setRouteHistoryJobId(null);
                    setRouteHistoryJobStatus('pending');
                    setRouteHistoryJobError(null);
                    try {
                      const res = await fetch('/api/route-stats-cache-unique/generate-unique-routes-async', { method: 'POST' });
                      const data = await res.json();
                      if (res.ok && data.jobId) {
                        setRouteHistoryJobId(data.jobId);
                        setRouteHistoryJobStatus('pending');
                        // 폴링 시작
                        let pollCount = 0;
                        const poll = async () => {
                          if (!data.jobId) return;
                          try {
                            const statusRes = await fetch(`/api/route-stats-cache-unique/generate-unique-routes-status?jobId=${data.jobId}`);
                            const statusData = await statusRes.json();
                            setRouteHistoryJobStatus(statusData.status);
                            if (statusData.status === 'success') {
                              setRouteStatsLoading(false);
                              alert(`자동완성 캐시(배차일보) 갱신 완료!\n처리된 데이터: ${statusData.totalProcessedRows}건`);
                              return;
                            } else if (statusData.status === 'error') {
                              setRouteStatsLoading(false);
                              setRouteHistoryJobError(statusData.error || '알 수 없는 오류');
                              alert('갱신 실패: ' + (statusData.error || '알 수 없는 오류'));
                              return;
                            } else {
                              pollCount++;
                              if (pollCount < 300) { // 최대 10분
                                setTimeout(poll, 2000);
                              } else {
                                setRouteStatsLoading(false);
                                setRouteHistoryJobError('타임아웃');
                                alert('갱신이 너무 오래 걸려 중단되었습니다.');
                              }
                            }
                            if (statusData.percent !== undefined) {
                              percentRef.current = statusData.percent;
                            }
                          } catch (e) {
                            setRouteStatsLoading(false);
                            setRouteHistoryJobError('상태 확인 실패');
                            alert('상태 확인 실패: ' + e);
                          }
                        };
                        setTimeout(poll, 2000);
                      } else {
                        setRouteStatsLoading(false);
                        alert('갱신 요청 실패: ' + (data.error || '알 수 없는 오류'));
                      }
                    } catch (err) {
                      setRouteStatsLoading(false);
                      alert('갱신 요청 실패: ' + err);
                    }
                  }}
                  disabled={loading || routeHistoryLoading || routeStatsLoading}
                >
                  {routeStatsLoading ? '생성중...' : '자동완성 캐시 생성'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Hidden elements */}
      <div style={{ display: 'none' }}>
        {/*
          엑셀 Import, 크롤링 테스트, 일괄삭제와 관련된 모든 엘리먼트를 제거합니다.
          이 부분은 현재 사용되지 않으며, 지속적인 타입 에러의 원인이었습니다.
        */}
      </div>

      {/* 로딩 오버레이 */}
      {(loading || routeHistoryLoading || routeStatsLoading) && (
        <div style={{
          position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.25)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 12, fontSize: 20, fontWeight: 700, color: '#1976d2', boxShadow: '0 2px 16px rgba(0,0,0,0.18)', minWidth: 340 }}>
            {loading ? '작업 진행중... 잠시만 기다려주세요'
              : routeHistoryLoading ? '운송구간(통합일보) 캐시 생성중...'
              : routeStatsLoading ? (routeHistoryJobStatus === 'running' ? '자동완성 캐시 생성중...' : '캐시 생성중...')
              : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelImport; 